import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { History, Download, Loader2 } from 'lucide-react'
import { Skeleton } from '../../components/ui/Skeleton'
import { Button } from '../../components/ui/button'
import { EmptyState } from '../../components/EmptyState'
import PullToRefresh from '../../components/PullToRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { useSessions } from '../../hooks/useSessions'
import { performRefresh } from '../../lib/refresh'
import { supabase } from '../../lib/supabaseClient'
import { buildWorkoutMarkdown, suggestedFilename } from '../../lib/workoutMarkdown'
import { downloadTextFile } from '../../lib/download'
import WorkoutBackHeader from './components/WorkoutBackHeader'
import SessionCard from './components/SessionCard'

export default function SessionHistory() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const { sessions, loading, refetch } = useSessions()
  const handleRefresh = useCallback(() => performRefresh([refetch]), [refetch])
  const [exporting, setExporting] = useState(false)

  // One-shot export: pulls every session in full (sets + cardio + the
  // joined exercise name) and serialises to markdown. Done on-click
  // rather than ambient so we don't haul every set into memory for
  // users who never tap the button.
  const handleExport = async () => {
    if (!user || exporting) return
    setExporting(true)
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id, date, session_type, duration_min, body_weight_kg, energy_rating, notes,
          exercise_logs(
            id, order_index, notes, exercise_id,
            exercise:exercises(name, muscle_group),
            sets(id, set_number, weight_kg, reps, rpe, is_warmup, is_failure)
          ),
          cardio_logs(id, duration_min, calories_estimated, cardio_type)
        `)
        .order('date', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) {
        console.error('[export] sessions fetch failed:', error)
        return
      }
      const md = buildWorkoutMarkdown(data || [], i18n.language)
      downloadTextFile(suggestedFilename(i18n.language), md)
    } finally {
      setExporting(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4 content-fade">
        <WorkoutBackHeader
          title={t('workout.history.title')}
          subtitle={t('workout.history.subtitle')}
          fallback="/workout"
        />
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    )
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-4 content-fade">
      <WorkoutBackHeader
        title={t('workout.history.title')}
        subtitle={t('workout.history.subtitle')}
        fallback="/workout"
      />

      {sessions.length === 0 ? (
        <EmptyState
          icon={History}
          title={t('workout.history.empty')}
        />
      ) : (
        <>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 me-1.5 animate-spin" />
              ) : (
                <Download className="w-4 h-4 me-1.5" />
              )}
              {t('workout.history.exportMarkdown')}
            </Button>
          </div>
          <div className="space-y-2">
            {sessions.map((s) => (
              <SessionCard key={s.id} session={s} />
            ))}
          </div>
        </>
      )}
    </div>
    </PullToRefresh>
  )
}
